import { IconMicrophone, IconPlayerStopFilled } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import type { AssistantVoiceEntityRef } from "~/shared/ai/assistant-voice";
import { ASSISTANT_VOICE_ENTITY_HEADER_MAX_BYTES, ASSISTANT_VOICE_MAX_RECORD_SECONDS } from "~/shared/ai/assistant-voice";
import type { VoiceCapturePhase } from "~/shared/voice/browser-voice-capture";
import {
  acquireVoiceCaptureLease,
  isBrowserVoiceRecordingSupported,
  ownsVoiceCaptureLease,
  pickVoiceRecordingMimeType,
  releaseVoiceCaptureLease,
  shouldIgnoreVoiceCaptureRequest,
} from "~/shared/voice/browser-voice-capture";
import { ActionIcon, Box, Loader, Tooltip } from "~/ui";

const assistantTranscriptionSchema = z.object({ transcript: z.string().trim().min(1) }).strict();
const assistantTranscriptionErrorSchema = z.object({ error: z.string().trim().min(1) }).passthrough();
export const ASSISTANT_VOICE_TAP_TARGET_SIZE = 44;

type RecordingSession = {
  id: number;
  mimeType: string;
  stream: MediaStream;
  recorder: MediaRecorder;
  chunks: Blob[];
  discard: boolean;
  cleanedUp: boolean;
};

type VoiceCaptureStream = {
  getTracks: () => Array<{ stop: () => void }>;
};

export const cleanupFailedVoiceCaptureSetup = <TSession extends { discard: boolean }>(
  stream: VoiceCaptureStream | null,
  session: TSession | null,
  cleanupSession: (failedSession: TSession) => void,
): void => {
  if (session) {
    session.discard = true;
    cleanupSession(session);
    return;
  }
  stream?.getTracks().forEach(track => track.stop());
};

interface AssistantVoiceInputProps {
  disabled: boolean;
  isActive?: boolean;
  entityRefs: AssistantVoiceEntityRef[];
  onTranscript: (transcript: string) => void;
  onBusyChange: (busy: boolean) => void;
}

const isAbortError = (error: unknown): boolean => error instanceof Error && error.name === "AbortError";

export const AssistantVoiceInput = ({ disabled, isActive, entityRefs, onTranscript, onBusyChange }: AssistantVoiceInputProps) => {
  const ownerRef = useRef(Symbol("assistant-voice-capture"));
  const mountedRef = useRef(true);
  const phaseRef = useRef<VoiceCapturePhase>("idle");
  const requestIdRef = useRef(0);
  const sessionRef = useRef<RecordingSession | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestAbortRef = useRef<AbortController | null>(null);
  const entityRefsRef = useRef(entityRefs);
  const onTranscriptRef = useRef(onTranscript);
  const [phase, setPhaseState] = useState<VoiceCapturePhase>("idle");
  const [error, setError] = useState<string | null>(null);

  entityRefsRef.current = entityRefs;
  onTranscriptRef.current = onTranscript;

  const setPhase = useCallback((next: VoiceCapturePhase) => {
    phaseRef.current = next;
    if (mountedRef.current) {
      setPhaseState(next);
    }
  }, []);

  useEffect(() => onBusyChange(phase !== "idle"), [onBusyChange, phase]);

  const clearStopTimer = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }, []);

  const cleanupSession = useCallback(
    (session: RecordingSession) => {
      if (session.cleanedUp) {
        return;
      }
      session.cleanedUp = true;
      clearStopTimer();
      session.stream.getTracks().forEach(track => track.stop());
      if (sessionRef.current === session) {
        sessionRef.current = null;
      }
    },
    [clearStopTimer],
  );

  const settleIdle = useCallback(() => {
    requestAbortRef.current = null;
    releaseVoiceCaptureLease(ownerRef.current);
    setPhase("idle");
  }, [setPhase]);

  const transcribeSession = useCallback(
    async (session: RecordingSession) => {
      cleanupSession(session);
      if (session.discard || !ownsVoiceCaptureLease(ownerRef.current)) {
        return;
      }

      const audio = new Blob(session.chunks, { type: session.mimeType });
      if (audio.size === 0) {
        setError("Didn't catch that. Try speaking closer to the microphone.");
        settleIdle();
        return;
      }

      const abortController = new AbortController();
      requestAbortRef.current = abortController;
      const refsHeader = JSON.stringify(entityRefsRef.current);
      const headers: Record<string, string> = { "Content-Type": session.mimeType };
      if (new TextEncoder().encode(refsHeader).byteLength <= ASSISTANT_VOICE_ENTITY_HEADER_MAX_BYTES) {
        headers["x-assistant-entity-refs"] = refsHeader;
      }

      try {
        const response = await fetch("/api/rest/assistant-transcribe", {
          method: "POST",
          headers,
          body: audio,
          signal: abortController.signal,
        });
        const payload: unknown = await response.json().catch(() => null);
        if (!response.ok) {
          const parsedError = assistantTranscriptionErrorSchema.safeParse(payload);
          throw new Error(parsedError.success ? parsedError.data.error : `Voice dictation failed (${response.status}).`);
        }
        const parsed = assistantTranscriptionSchema.safeParse(payload);
        if (!parsed.success) {
          throw new Error("Voice dictation returned an invalid response.");
        }
        if (
          !abortController.signal.aborted &&
          !session.discard &&
          requestIdRef.current === session.id &&
          ownsVoiceCaptureLease(ownerRef.current) &&
          mountedRef.current
        ) {
          onTranscriptRef.current(parsed.data.transcript);
          setError(null);
        }
      } catch (caught) {
        if (!isAbortError(caught) && !abortController.signal.aborted && mountedRef.current) {
          setError(
            caught instanceof TypeError
              ? "Couldn't reach voice dictation. Check your connection and try again."
              : caught instanceof Error
                ? caught.message
                : "Voice dictation failed. Please try again.",
          );
        }
      } finally {
        if (requestAbortRef.current === abortController) {
          settleIdle();
        }
      }
    },
    [cleanupSession, settleIdle],
  );

  const stopRecording = useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.recorder.state !== "recording" || phaseRef.current !== "recording") {
      return;
    }
    clearStopTimer();
    setPhase("transcribing");
    session.recorder.stop();
  }, [clearStopTimer, setPhase]);

  const cancel = useCallback(() => {
    requestIdRef.current += 1;
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    const session = sessionRef.current;
    if (session) {
      session.discard = true;
      if (session.recorder.state !== "inactive") {
        session.recorder.stop();
      }
      cleanupSession(session);
    }
    releaseVoiceCaptureLease(ownerRef.current);
    setPhase("idle");
  }, [cleanupSession, setPhase]);

  const start = useCallback(async () => {
    if (disabled || phaseRef.current !== "idle") {
      return;
    }
    setError(null);
    if (!isBrowserVoiceRecordingSupported()) {
      setError("Voice dictation isn't available in this browser. On Safari, use keyboard dictation or update Safari.");
      return;
    }
    if (!acquireVoiceCaptureLease(ownerRef.current)) {
      setError("Voice dictation is already active in another Assistant window.");
      return;
    }

    const mimeType = pickVoiceRecordingMimeType();
    if (!mimeType) {
      releaseVoiceCaptureLease(ownerRef.current);
      setError("Voice dictation isn't available in this browser. On Safari, use keyboard dictation or update Safari.");
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPhase("permission");

    let stream: MediaStream | null = null;
    let session: RecordingSession | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (
        shouldIgnoreVoiceCaptureRequest({
          requestId,
          activeRequestId: requestIdRef.current,
          currentPhase: phaseRef.current,
          owner: ownerRef.current,
        })
      ) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      const activeSession: RecordingSession = {
        id: requestId,
        mimeType,
        stream,
        recorder,
        chunks: [],
        discard: false,
        cleanedUp: false,
      };
      session = activeSession;
      sessionRef.current = activeSession;
      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          activeSession.chunks.push(event.data);
        }
      };
      recorder.onstop = () => void transcribeSession(activeSession);
      recorder.start(250);
      setPhase("recording");
      stopTimerRef.current = setTimeout(stopRecording, ASSISTANT_VOICE_MAX_RECORD_SECONDS * 1_000);
    } catch {
      cleanupFailedVoiceCaptureSetup(stream, session, cleanupSession);
      if (requestId === requestIdRef.current && ownsVoiceCaptureLease(ownerRef.current)) {
        setError("Microphone access is blocked. Enable it in your browser settings and try again.");
        settleIdle();
      }
    }
  }, [cleanupSession, disabled, setPhase, settleIdle, stopRecording, transcribeSession]);

  useEffect(() => {
    if (isActive === false || disabled) {
      cancel();
    }
  }, [cancel, disabled, isActive]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancel();
    };
  }, [cancel]);

  const action = () => {
    if (phase === "recording") {
      stopRecording();
    } else if (phase === "permission" || phase === "transcribing") {
      cancel();
    } else {
      void start();
    }
  };
  const label =
    phase === "recording"
      ? "Stop voice recording"
      : phase === "permission"
        ? "Cancel microphone request"
        : phase === "transcribing"
          ? "Cancel voice transcription"
          : "Dictate message";

  return (
    <Box>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .assistant-voice-control { transition: none !important; }
        }
      `}</style>
      <Tooltip label={error ?? label} opened={error ? true : undefined} withArrow position="top-end">
        <ActionIcon
          aria-label={label}
          className="assistant-voice-control"
          size={ASSISTANT_VOICE_TAP_TARGET_SIZE}
          radius="xl"
          color={phase === "recording" ? "red" : "gray"}
          variant={phase === "recording" ? "filled" : "subtle"}
          disabled={disabled}
          onClick={action}
          style={{ transition: "all 0.15s ease" }}
        >
          {phase === "transcribing" || phase === "permission" ? (
            <Loader size={18} color="gray" />
          ) : phase === "recording" ? (
            <IconPlayerStopFilled size={17} />
          ) : (
            <IconMicrophone size={19} />
          )}
        </ActionIcon>
      </Tooltip>
      {error && (
        <Box role="alert" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
          {error}
        </Box>
      )}
    </Box>
  );
};
